import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function VatRates() {
  const navigate = useNavigate();
  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadVatRates();
  }, []);

  const loadVatRates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vat-rates');
      setVatRates(response.data.vat_rates || []);
      setError('');
    } catch (err) {
      console.error('Failed to load VAT rates:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to load VAT rates';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/vat-rates/new');
  };

  const handleEdit = (rate) => {
    navigate(`/vat-rates/${rate.id}/edit`);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this VAT rate?')) {
      return;
    }

    try {
      await api.delete(`/vat-rates/${rateId}`);
      setVatRates(vatRates.filter(r => r.id !== rateId));
    } catch (err) {
      console.error('Failed to delete VAT rate:', err);
      alert(err.response?.data?.error || 'Failed to delete VAT rate');
    }
  };

  if (loading && vatRates.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">VAT Rates</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              💰 VAT Rates
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage tax and VAT rates for products
            </p>
          </div>
          <button onClick={handleCreate} className="btn btn-primary">➕ Add VAT Rate</button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError('')}>
            {error}
          </div>
        )}

        {/* VAT Rates Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Code</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Rate (%)</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Country</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vatRates.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  No VAT rates found. Create your first VAT rate to get started.
                </td>
              </tr>
            ) : (
              vatRates.map((rate) => (
                <tr key={rate.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '0.75rem' }}>
                    <div style={{ fontWeight: '500' }}>{rate.name}</div>
                    {rate.is_default && (
                      <span className="badge badge-primary" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Default
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>{rate.code}</span>
                  </td>
                  <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                    {parseFloat(rate.rate).toFixed(2)}%
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {rate.country_code ? (
                      <span style={{ fontSize: '0.875rem' }}>{rate.country_code}</span>
                    ) : (
                      <span style={{ color: 'var(--gray-400)' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className={`badge badge-${rate.is_active ? 'success' : 'secondary'}`}>
                      {rate.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button
                      onClick={() => handleEdit(rate)}
                      className="btn btn-sm btn-outline"
                      style={{ marginRight: '0.5rem' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rate.id)}
                      className="btn btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VatRates;
